import * as React from "react";
import {
  DropdownMenu as ShadcnDropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
} from "../dropdown-menu";
import { cn } from "@/lib/utils";

// Context for passing dropdown props
interface DropdownContextValue {
  showArrow?: boolean;
  arrow?: React.ReactNode;
  classNames?: {
    base?: string;
    content?: string;
    trigger?: string;
  };
}

const DropdownContext = React.createContext<DropdownContextValue>({});

// Dropdown (Root) with showArrow, arrow, classNames, onOpenChange support
export interface DropdownProps extends React.ComponentPropsWithoutRef<
  typeof ShadcnDropdownMenu
> {
  showArrow?: boolean;
  arrow?: React.ReactNode;
  classNames?: {
    base?: string;
    content?: string;
    trigger?: string;
  };
  onOpenChange?: (open: boolean) => void;
}

const Dropdown: React.FC<DropdownProps> = ({
  showArrow,
  arrow,
  classNames,
  onOpenChange,
  children,
  ...props
}) => {
  return (
    <DropdownContext.Provider value={{ showArrow, arrow, classNames }}>
      <ShadcnDropdownMenu onOpenChange={onOpenChange} {...props}>
        {children}
      </ShadcnDropdownMenu>
    </DropdownContext.Provider>
  );
};

export const DropdownTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuTrigger>
>(({ className, ...props }, ref) => {
  const { classNames } = React.useContext(DropdownContext);
  return (
    <DropdownMenuTrigger
      ref={ref}
      className={cn(classNames?.trigger, className)}
      {...props}
    />
  );
});
DropdownTrigger.displayName = "DropdownTrigger";

// DropdownContent with arrow support
const DropdownContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuContent>
>(({ className, children, ...props }, ref) => {
  const { showArrow, arrow, classNames } = React.useContext(DropdownContext);

  return (
    <DropdownMenuContent
      ref={ref}
      className={cn(classNames?.content, className)}
      {...props}
    >
      {showArrow && (
        <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-white border-l border-t border-secondary-light rotate-45 z-10" />
      )}
      {arrow && (
        <div className="absolute -top-1 left-1/2 -translate-x-1/2 z-10">
          {arrow}
        </div>
      )}
      {children}
    </DropdownMenuContent>
  );
});
DropdownContent.displayName = "DropdownContent";

// Export all components
export { Dropdown, DropdownContent };
export const DropdownItem = DropdownMenuItem;
export const DropdownCheckboxItem = DropdownMenuCheckboxItem;
export const DropdownRadioItem = DropdownMenuRadioItem;
export const DropdownLabel = DropdownMenuLabel;
export const DropdownSeparator = DropdownMenuSeparator;
export const DropdownShortcut = DropdownMenuShortcut;
export const DropdownGroup = DropdownMenuGroup;
export const DropdownPortal = DropdownMenuPortal;
export const DropdownSub = DropdownMenuSub;
export const DropdownSubContent = DropdownMenuSubContent;
export const DropdownSubTrigger = DropdownMenuSubTrigger;
export const DropdownRadioGroup = DropdownMenuRadioGroup;

// DropdownMenu (Content) with variant support
export interface DropdownMenuProps extends React.ComponentPropsWithoutRef<
  typeof DropdownMenuContent
> {
  variant?: "light" | "solid" | "bordered" | "flat" | "faded" | "shadow";
  classNames?: {
    base?: string;
  };
}

const DropdownMenu = React.forwardRef<
  React.ElementRef<typeof DropdownMenuContent>,
  DropdownMenuProps
>(({ variant, classNames, className, ...props }, ref) => {
  const variantClasses = {
    light: "bg-white",
    solid: "bg-gray-900 text-white",
    bordered: "bg-white border-1.5",
    flat: "bg-transparent",
    faded: "bg-gray-100",
    shadow: "bg-white shadow-lg",
  };

  // Use DropdownContent to get arrow support from context
  return (
    <DropdownContent
      ref={ref}
      className={cn(
        variant && variantClasses[variant],
        classNames?.base,
        className
      )}
      {...props}
    />
  );
});
DropdownMenu.displayName = "DropdownMenu";

// Also export with original names for compatibility
export {
  ShadcnDropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
};

// Export DropdownMenu as the content wrapper
export { DropdownMenu };
